import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
// import { globalApi } from "../../api/global";
import utils from "../../utils";
import { ROUTES } from "../../types/routes";
import { useI18n } from "../../i18n/hooks";
import { DownloadAppBarProps } from "./index.types";
import { Button } from "../Base/Button";
import { Icon } from "../Icon";
import { setGlobalValue } from "../../stores/GlobalState";
import { useUserDataFlattened } from "../../modules/user";
import {
  useEnableDownloadAppFloatingOnHomeTop,
  usePlatformLogo,
} from "../../modules/homeDisplay/hooks";
import "./DownloadAppBar.scss";
import { showSafariAddHomeScreen } from "../../stores/GlobalState";

// import headerAppDownloadBarBackground from "@/assets/appDownload/headerAppDownloadBarBackground.png";
import { OptimizedImage } from "../Base";

// interface AppInfo {
//   apkLink?: string;
//   version?: string;
//   size?: string;
// }

export const DownloadAppBar: React.FC<DownloadAppBarProps> = ({
  className = "",
}) => {
  const { t } = useI18n();
  const enableFloatingOnHomeTop = useEnableDownloadAppFloatingOnHomeTop();
  const platformLogo = usePlatformLogo();
  const [topNavShow, setTopNavShow] = useState(false);
  // const [infoIos, setInfoIos] = useState<AppInfo | null>(null);
  const location = useLocation();
  const { isAuthenticated } = useUserDataFlattened();

  // 从 localstore 读取关闭状态，默认为 true（显示）
  const getInitialVisible = () => {
    try {
      const stored = utils.getObject("downloadAppBarVisible");
      return stored !== false; // 只有明确设置为 false 才隐藏，其他情况都显示
    } catch {
      return true;
    }
  };

  const [isVisible, setIsVisible] = useState(getInitialVisible);

  // 判断设备类型（仅用于下载时判断）
  const getDeviceType = (): "Android" | "IOS" | "PC" => {
    const u = navigator.userAgent;
    const isAndroid = u.indexOf("Android") > -1 || u.indexOf("Adr") > -1;
    const isIOS = !!u.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/);
    if (isAndroid) {
      return "Android";
    } else if (isIOS) {
      return "IOS";
    } else {
      return "PC";
    }
  };

  // 获取iOS应用信息 - 只在需要时调用
  // const fetchIosInfo = async (): Promise<AppInfo | null> => {
  //   if (infoIos) return infoIos; // 如果已经有信息就返回现有信息
  //   try {
  //     const res = await globalApi.getIosAppInfo();
  //     if (res?.data?.model) {
  //       setInfoIos(res.data.model);
  //       return res.data.model; // 返回获取到的数据
  //     }
  //   } catch (error) {
  //     console.error("get ios app info failed:", error);
  //   }
  //   return null;
  // };

  // 同步最终的显示状态到全局状态
  useEffect(() => {
    setGlobalValue("downloadAppBarVisible", isVisible);
  }, [isVisible]);

  // 控制显示逻辑（需配置 enableDownloadAppFloatingOnHomeTop 为 true 才在首页顶部显示）
  useEffect(() => {
    if (!enableFloatingOnHomeTop) {
      setTopNavShow(false);
      setGlobalValue("downloadAppBarVisible", false);
      return;
    }

    const isHomePage =
      location.pathname === ROUTES.HOME ||
      location.pathname === ROUTES.HOME_ALT;

    // 如果不是首页，不显示
    if (!isHomePage) {
      setTopNavShow(false);
      return;
    }

    // 如果已安装APP（在APP内部），不显示，并且设置全局状态为 false
    if (utils.isApp()) {
      setTopNavShow(false);
      setIsVisible(false);
      setGlobalValue("downloadAppBarVisible", false);
      return;
    }

    setTopNavShow(true);
  }, [location.pathname, isVisible, enableFloatingOnHomeTop]);

  // 下载/安装逻辑 - 根据设备类型下载对应版本
  const downloadInstall = () => {
    const deviceType = getDeviceType();

    if (deviceType === "IOS") {
      // iOS设备下载iOS版本
      downloadIos();
    } else if (deviceType === "Android") {
      // Android设备下载Android版本
      downloadAndroid();
    } else {
      // PC或其他设备，优先Android，其次iOS
      downloadAndroid();
    }
  };

  // iOS下载逻辑
  const downloadIos = async () => {
    try {
      // // 如果没有iOS信息，先获取
      // let appInfoIos = infoIos;
      // if (!appInfoIos) {
      //   appInfoIos = await fetchIosInfo();
      // }

      if (platformLogo?.enableIosDownload && platformLogo.iosDownloadLink) {
        const src = platformLogo.iosDownloadLink;
        // 优先尝试直接打开链接（iOS设备推荐方式）
        if (utils.checkIsIos()) {
          try {
            window.location.href = src;
            return;
          } catch {
            console.warn("Direct link failed, falling back to form method");
          }
        }

        // 回退到表单提交方式 - 兼容所有iOS版本
        const form = document.createElement("form");
        try {
          // 检查是否支持现代URL API（iOS 10.3+）
          if (
            typeof URL !== "undefined" &&
            typeof URLSearchParams !== "undefined"
          ) {
            const url = new URL(src);
            const params = new URLSearchParams(url.search);
            for (const pair of params.entries()) {
              const input = document.createElement("input");
              input.type = "hidden";
              input.name = pair[0];
              input.value = pair[1];
              form.appendChild(input);
            }
          } else {
            // 兼容iOS 8-10.2：手动解析URL参数
            const urlParts = src.split("?");
            if (urlParts.length > 1) {
              const queryString = urlParts[1];
              const pairs = queryString.split("&");
              for (let i = 0; i < pairs.length; i++) {
                const pair = pairs[i].split("=");
                if (pair.length === 2) {
                  const input = document.createElement("input");
                  input.type = "hidden";
                  input.name = decodeURIComponent(pair[0]);
                  input.value = decodeURIComponent(pair[1]);
                  form.appendChild(input);
                }
              }
            }
          }
        } catch {
          console.warn("URL parsing failed");
          // 如果URL解析失败，直接使用原始链接
          window.location.href = src;
          return;
        }
        form.action = src.split("?")[0]; // 移除查询参数，因为已经作为hidden input处理
        form.method = "get";
        form.target = "_blank";
        document.getElementsByTagName("body")[0].appendChild(form);
        form.submit();
        setTimeout(() => {
          try {
            document.body.removeChild(form);
          } catch {
            // 忽略移除错误
          }
        }, 1500);
      } else {
        showSafariAddHomeScreen(true);
      }
    } catch (error) {
      console.error("download ios failed:", error);
    }
  };

  // Android下载逻辑
  const downloadAndroid = () => {
    if (platformLogo?.appDownloadMethod === "GP") {
      if (platformLogo.gpLink) {
        window.open(platformLogo.gpLink);
      }
      return;
    }

    if (platformLogo?.appDownloadMethod === "APK") {
      if (platformLogo.apkFile) {
        const src = utils.getApkDownloadUrl(platformLogo.apkFile);
        const form = document.createElement("form");
        try {
          const url = new URL(src);
          const params = new URLSearchParams(url.search);
          for (const pair of params.entries()) {
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = pair[0];
            input.value = pair[1];
            form.appendChild(input);
          }
        } catch {
          // 忽略URL解析错误
        }
        form.action = src;
        document.getElementsByTagName("body")[0].appendChild(form);
        form.submit();
        setTimeout(() => {
          try {
            document.body.removeChild(form);
          } catch {
            // 忽略移除错误
          }
        }, 1500);
      }
    }
  };

  // 关闭bar
  const handleClose = () => {
    setIsVisible(false);
    // 只有登录状态下关闭时才更新全局状态和持久化
    // 未登录时关闭不更新全局状态，保持Header的marginTop样式
    if (isAuthenticated) {
      setGlobalValue("downloadAppBarVisible", false);
      utils.setObject("downloadAppBarVisible", false);
    }
  };

  // 配置关闭顶部下载条时不显示
  if (!enableFloatingOnHomeTop) {
    return null;
  }

  // 如果被用户关闭，不显示
  if (!isVisible) {
    return null;
  }

  // 如果不符合显示条件，不显示
  if (!topNavShow) {
    return null;
  }

  const noticeText = platformLogo?.appDownloadTitle;

  return (
    <div className={`downloadAppBar ${className}`}>
      {/* <img
        className="downloadAppBar__iconBackground"
        src={headerAppDownloadBarBackground}
        alt="Icon Background"
      /> */}
      <div className="downloadAppBar__inner">
        {/* 左边图标 */}
        <div className="downloadAppBar__iconWrapper">
          <OptimizedImage
            className="downloadAppBar__icon"
            src={utils.getImageUrl(window._config?.icon || "")}
            alt="App Icon"
            loading="eager"
            quality="medium"
          />
        </div>

        {/* 中间文本 */}
        {noticeText && (
          <div className="downloadAppBar__title">{noticeText}</div>
        )}

        {/* 右侧按钮组 */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            flexShrink: 0,
          }}
        >
          {/* 下载按钮 */}
          <Button
            variant="secondary"
            className="downloadAppBar__button"
            onClick={downloadInstall}
            shape="rounded"
            size="sm"
          >
            {t("common.download")}
          </Button>

          {/* 关闭按钮 */}
          <button
            className="downloadAppBar__closeBtn"
            onClick={handleClose}
            aria-label="Close"
          >
            <Icon name="shareAppBarClose" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default DownloadAppBar;
